import React, { useEffect, useState } from 'react';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select, message } from 'antd';
import dayjs from 'dayjs';
import TimeSelect from './TimeSelect';
import api from '../api';

export const VARIANCE_TYPES = [
  'Client Early Release',
  'Client approved OT (added to schedule, billed)',
  'Client approved OT (Alert Coverage - billed)',
  'Text Alert Coverage (not billed)',
  'Extra Break Award',
  'Paycor Correction',
  'On-Call Notification',
  'Outage Notification',
  'Other',
];
export const VARIANCE_TYPE_OPTIONS = VARIANCE_TYPES.map((v) => ({ label: v, value: v }));
export const REMOVE_POINTS_OPTIONS = ['N/A', 'Points to be removed', 'Points have been removed'].map((v) => ({ label: v, value: v }));
export const VARIANCE_STATUS_OPTIONS = ['Open', 'Complete', 'Canceled'].map((v) => ({ label: v, value: v }));
export const VARIANCE_STATUS_COLOR = { Open: 'blue', Complete: 'green', Canceled: 'default' };

// '7:05 PM' -> dayjs. Parsed by hand: dayjs needs the customParseFormat
// plugin to read a format string, and the app does not load it.
export function parseTime(str) {
  const m = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec((str || '').trim());
  if (!m) return undefined;
  const h = (Number(m[1]) % 12) + (m[3].toUpperCase() === 'PM' ? 12 : 0);
  return dayjs().startOf('day').hour(h).minute(Number(m[2]));
}

// Form values -> API body.
export function toVarianceBody(values) {
  return {
    user_id:           values.user_id,
    team_id:           values.team_id || null,
    variance_type:     values.variance_type,
    variance_date:     values.variance_date?.format('YYYY-MM-DD'),
    shift_start:       values.shift_start?.format('h:mm A') || null,
    shift_end:         values.shift_end?.format('h:mm A') || null,
    billable_ot_hours: values.billable_ot_hours ?? null,
    remove_points:     values.remove_points || 'N/A',
    notes:             values.notes || null,
    billing_notes:     values.billing_notes || null,
    status:            values.status || 'Open',
  };
}

// API row -> form values.
export function fromVarianceRow(v) {
  return {
    user_id:           v.UserID,
    team_id:           v.TeamID || undefined,
    variance_type:     v.VarianceType,
    variance_date:     v.VarianceDate ? dayjs(String(v.VarianceDate).slice(0, 10)) : undefined,
    shift_start:       parseTime(v.ShiftStart),
    shift_end:         parseTime(v.ShiftEnd),
    billable_ot_hours: v.BillableOTHours ?? null,
    remove_points:     v.RemovePoints || 'N/A',
    notes:             v.Notes || '',
    billing_notes:     v.BillingNotes || '',
    status:            v.Status,
  };
}

/**
 * The Schedule Variance form fields, shared by the create popup and the edit
 * page. Employee and Team work as on the absence form: the employee list is
 * limited to the user's Visible Teams, picking one fills in their team, and
 * the team can only be chosen by hand when the employee has none. The server
 * stamps the employee's current team either way. Both are locked once the
 * variance exists (lockEmployee): a wrong employee is canceled, not changed.
 */
export default function ScheduleVarianceFields({ form, open = true, initialEmployee, lockEmployee = false, showStatus = true }) {
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [employeeHasTeam, setEmployeeHasTeam] = useState(!!initialEmployee?.teamId);
  const [employeeSelected, setEmployeeSelected] = useState(!!initialEmployee);

  useEffect(() => {
    if (!open) return;
    api.get('/api/schedule-variances/employees')
      .then((r) => setEmployees((r.data || []).map((u) => ({
        label:  u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
        value:  u.ID,
        teamId: u.TeamID || null,
      }))))
      .catch(() => message.error('Failed to load employees.'));
    api.get('/api/teams').then((r) => setTeams(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    setEmployeeSelected(!!initialEmployee);
    setEmployeeHasTeam(!!initialEmployee?.teamId);
  }, [initialEmployee]);

  // An employee already on the record stays selectable even if they have
  // since left the user's visible teams or gone inactive.
  const employeeOptions = initialEmployee && !employees.some((e) => e.value === initialEmployee.value)
    ? [initialEmployee, ...employees]
    : employees;

  return (
    <>
      {/* Status first, on its own line. The record page shows it beside Save
          instead (showStatus false), as the error page does. */}
      {showStatus && (
        <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Required' }]}>
          <Select options={VARIANCE_STATUS_OPTIONS} style={{ width: 200 }} />
        </Form.Item>
      )}

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label="Employee" name="user_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder="Select employee..."
              options={employeeOptions}
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              disabled={lockEmployee}
              onChange={(id) => {
                setEmployeeSelected(!!id);
                const opt = employeeOptions.find((u) => u.value === id);
                form.setFieldValue('team_id', opt?.teamId || undefined);
                setEmployeeHasTeam(!!opt?.teamId);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Team" name="team_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder="Select team..."
              options={teams.map((t) => ({ label: t.Name, value: t.ID }))}
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              disabled={lockEmployee || !employeeSelected || employeeHasTeam}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Full width: the type names are long and must be read in full. */}
      <Form.Item label="Variance Type" name="variance_type" rules={[{ required: true, message: 'Required' }]}>
        <Select options={VARIANCE_TYPE_OPTIONS} placeholder="Select type..." style={{ width: '100%' }} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item label="Date of Variance" name="variance_date" rules={[{ required: true, message: 'Required' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Shift Start (hours affected)" name="shift_start">
            <TimeSelect />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Shift End (hours affected)" name="shift_end">
            <TimeSelect />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label="Billable OT Hours" name="billable_ot_hours">
            <InputNumber min={0} max={24} step={0.25} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Remove Points" name="remove_points" rules={[{ required: true, message: 'Required' }]}>
            <Select options={REMOVE_POINTS_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Notes" name="notes">
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item label="Billing Notes" name="billing_notes">
        <Input.TextArea rows={2} />
      </Form.Item>
    </>
  );
}
